import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { scrollToId } from "@/components/Navbar";
import { useLang } from "@/i18n/LanguageContext";


export default function Hero({ settings }) {
  const { lang, t } = useLang();
  // Si la boot shell ya mostró el hero, no repetir la animación de entrada
  // (así el hero real pinta completo apenas monta y no retrasa el LCP).
  const booted = typeof document !== "undefined" && !!document.getElementById("boot-shell");
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);
  const scale = useTransform(scrollY, [0, 900], [1.05, 1.18]);

  const title = lang === "es" && settings?.hero_title ? settings.hero_title : t("hero.title");
  const words = title.split(" ");
  const lines = [];
  for (let i = 0; i < words.length; i += 2) lines.push(words.slice(i, i + 2));

  const subtitle = lang === "es" && settings?.hero_subtitle ? settings.hero_subtitle : t("hero.subtitle");
  const stats = t("hero.stats");

  return (
    <section id="inicio" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="/hero-amalfi-1366.webp"
          srcSet="/hero-amalfi-768.webp 768w, /hero-amalfi-1366.webp 1366w, /hero-amalfi-1920.webp 1920w"
          sizes="100vw"
          alt="Costa de Amalfi"
          className="w-full h-full object-cover"
          fetchPriority="high"
          loading="eager"
          decoding="async"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B2E4A]/90 via-[#0B2E4A]/55 to-[#0B2E4A]/15" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B2E4A]/75 via-transparent to-[#0B2E4A]/35" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pb-16 sm:pb-24 pt-40 w-full">
        <motion.div
          initial={booted ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#D9A854]/40 bg-[#0B2E4A]/40 backdrop-blur-md px-4 py-2 mb-8"
          data-testid="hero-eyebrow"
        >
          <Sparkles size={14} className="text-[#D9A854]" />
          <span className="text-xs sm:text-sm italic font-semibold tracking-wide text-[#FAF6EE]">
            {t("hero.signature")}
          </span>
        </motion.div>

        <h1 className="font-display font-medium text-[#FAF6EE] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl text-balance">
          {lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={booted ? false : { y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.13, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.map((w, j) => {
                  const isLast = i * 2 + j === words.length - 1;
                  return (
                    <span key={j} className={isLast ? "italic text-[#D9A854]" : undefined}>
                      {w}
                      {j < line.length - 1 ? " " : ""}
                    </span>
                  );
                })}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={booted ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-[#F1E7D6]/85 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {subtitle}
        </motion.p>

        <motion.div
          initial={booted ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/quiz"
            data-testid="hero-cta-primary"
            className="bg-[#D9A854] hover:bg-[#C99944] text-[#0B2E4A] font-semibold px-8 py-4 rounded-full transition-all duration-200 flex items-center gap-2 shadow-xl shadow-[#D9A854]/25 hover:scale-[1.03] active:scale-95"
          >
            {t("hero.ctaPrimary")} <ArrowRight size={17} />
          </Link>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToId("servicios")}
            className="border border-white/40 bg-white/10 backdrop-blur-md hover:bg-white/20 text-[#FAF6EE] font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:scale-[1.03] active:scale-95"
          >
            {t("hero.ctaSecondary")}
          </button>
        </motion.div>

        <motion.div
          initial={booted ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-14 max-w-3xl border-t border-white/15 pt-8"
          data-testid="hero-stats"
        >
          <div className="flex flex-col sm:flex-row gap-7 sm:gap-0">
            {Array.isArray(stats) && stats.map(([n, l], i) => (
              <div key={l} className={`sm:flex-1 ${i > 0 ? "sm:border-l sm:border-white/15 sm:pl-10" : ""}`}>
                <div className="font-display font-medium text-4xl sm:text-5xl text-[#D9A854] leading-none">{n}</div>
                <div className="text-sm text-[#F1E7D6]/70 mt-2.5 leading-snug">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
